/**
 * Visitantes (1.0 AdminVisitors + 27/10): cifras de `admin-visitorStats` en el rango que elijas:
 * únicos y páginas por día, visitantes nuevos por país (exacto), únicos diarios por país y altas por
 * ciudad del perfil. El país lo pone Hosting; la ciudad de un visitante anónimo no se sabe (sin IP).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { api } from '@nuvvu/shared/api';
import type { VisitorStatsOutput } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { card, muted, Notice, RangePicker, rangeOf, Table, td, type Range } from '../lib/ui.js';

const n = (v: number): string => v.toLocaleString('es');
const regions = new Intl.DisplayNames(['es'], { type: 'region' });
const country = (code: string): string => {
  if (code.length !== 2) return code;
  try {
    return `${regions.of(code) ?? code} (${code})`;
  } catch {
    return code;
  }
};

export function VisitorsPage(): ReactElement {
  const [range, setRange] = useState<Range>(rangeOf(30));
  const [s, setS] = useState<VisitorStatsOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setS(null);
    setError(null);
    call(api.admin.visitorStats, { from: range.from, to: range.to })
      .then(setS)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  }, [range]);
  const cards: [string, number, string?][] = s
    ? [
        ['Visitantes nuevos', s.range.new_visitors, 'primera visita en el rango'],
        ['Visitas únicas diarias', s.range.daily_unique_sum, 'suma de cada día'],
        ['Páginas vistas', s.range.page_views],
        ['Activos (30 días)', s.unique_visitors_30d],
        ['Visitantes únicos (total)', s.unique_visitors_total, 'desde el principio'],
        ['Usuarios registrados', s.registered_users],
      ]
    : [];
  return (
    <>
      <h1 style={{ margin: 0 }}>Visitantes</h1>
      <RangePicker value={range} onChange={setRange} />
      <p style={muted}>
        {range.label}. Visitante = identificador anónimo del navegador (sin IP ni datos personales);
        el país lo pone Google al servir la web. Los países por día se guardan desde el 27/10/2026.
      </p>
      <Notice text={error} error />
      {!s && !error && <p style={muted}>Cargando…</p>}
      {s && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 10,
            }}
          >
            {cards.map(([label, v, sub]) => (
              <section key={label} style={{ ...card, gap: 4 }}>
                <span style={muted}>{label}</span>
                <strong style={{ fontSize: 24 }}>{n(v)}</strong>
                {sub && <span style={{ ...muted, fontSize: 12 }}>{sub}</span>}
              </section>
            ))}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 12,
              alignItems: 'start',
            }}
          >
            <section style={card}>
              <strong>Visitantes nuevos por país</strong>
              <Table head={['País', 'Nuevos']}>
                {s.range.new_by_country.map((c) => (
                  <tr key={c.code}>
                    <td style={td}>{country(c.code)}</td>
                    <td style={td}>{n(c.count)}</td>
                  </tr>
                ))}
                {s.range.new_by_country.length === 0 && (
                  <tr>
                    <td style={td} colSpan={2}>
                      Ninguno en el rango.
                    </td>
                  </tr>
                )}
              </Table>
            </section>
            <section style={card}>
              <strong>Visitas únicas diarias por país</strong>
              <Table head={['País', 'Únicos']}>
                {(s.range.countries.length > 0 ? s.range.countries : s.top_countries).map((c) => (
                  <tr key={c.code}>
                    <td style={td}>{country(c.code)}</td>
                    <td style={td}>{n(c.unique_visitors)}</td>
                  </tr>
                ))}
              </Table>
              {s.range.countries.length === 0 && (
                <span style={muted}>Aún sin datos por día en este rango: se muestra el total.</span>
              )}
            </section>
            <section style={card}>
              <strong>Altas por ciudad (perfil)</strong>
              <Table head={['País', 'Ciudad', 'Altas']}>
                {s.range.signups_by_city.map((c) => (
                  <tr key={`${c.country}|${c.city}`}>
                    <td style={td}>{country(c.country)}</td>
                    <td style={td}>{c.city}</td>
                    <td style={td}>{n(c.count)}</td>
                  </tr>
                ))}
                {s.range.signups_by_city.length === 0 && (
                  <tr>
                    <td style={td} colSpan={3}>
                      Ninguna en el rango.
                    </td>
                  </tr>
                )}
              </Table>
            </section>
            <section style={card}>
              <strong>Por día</strong>
              <Table head={['Día', 'Únicos', 'Páginas']}>
                {s.days.map((d) => (
                  <tr key={d.date}>
                    <td style={td}>{d.date}</td>
                    <td style={td}>{n(d.unique_visitors)}</td>
                    <td style={td}>{n(d.page_views)}</td>
                  </tr>
                ))}
              </Table>
            </section>
          </div>
        </>
      )}
    </>
  );
}
